import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';

import { filter, map, startWith } from 'rxjs/operators';

interface Crumb {
  label: string;
  url: string | null;
}

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'navigation',
    'aria-label': 'Breadcrumb',
    class: 'app-breadcrumbs'
  }
})
export class BreadcrumbsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly nav = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.route.root.snapshot)
    ),
    { initialValue: this.route.root.snapshot }
  );

  protected readonly crumbs = computed<Crumb[]>(() => this.collect(this.nav()));

  private collect(root: ActivatedRouteSnapshot): Crumb[] {
    const out: Crumb[] = [];
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = root.firstChild;
    while (node) {
      const path = node.url.map(u => u.path).join('/');
      if (path) segments.push(path);
      const label = node.data['breadcrumb'] as string | undefined;
      if (label) {
        out.push({ label, url: '/' + segments.join('/') });
      }
      node = node.firstChild;
    }
    if (out.length) out[out.length - 1].url = null;
    return out;
  }
}
